import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Cell, Pie, PieChart, ResponsiveContainer, Sector } from 'recharts';
import {
  ARTS_COLOR_FALLBACKS,
  ARTS_KEYS,
  getArtsColors,
  hexToRgba,
} from '../../arts-constants';
import Loader from '../../../components/common/Loader';

const HEATMAP_MIN_OPACITY = 0.08;
const HEATMAP_MAX_OPACITY = 1.0;
const HEATMAP_SIGMA_MIN = 0.65;
const HEATMAP_SIGMA_MAX = 1.3;
const HEATMAP_SIGMA_DEFAULT = 0.95;
const HEATMAP_SIGMA_FACTOR = 0.3;
const HEATMAP_GAMMA = 0.55;
const HEATMAP_RADIUS = 2;

// Adaptive sigma: more data → tighter (more precision), less data → wider (more smoothing).
const computeAdaptiveSigma = (total) => {
  if (total <= 0) return HEATMAP_SIGMA_DEFAULT;
  const log = Math.log10(total + 1);
  const sigma = HEATMAP_SIGMA_MAX - log * HEATMAP_SIGMA_FACTOR;
  return Math.max(HEATMAP_SIGMA_MIN, Math.min(HEATMAP_SIGMA_MAX, sigma));
};

// Determine which quadrant a cell (col, row) belongs to.
// col 0..9 left→right (x), row 0..9 top→bottom (inverted y).
// NW-2543 layout: A (top-left) | S (top-right) | R (bot-left) | T (bot-right).
const getCellQuadrant = (col, row) => {
  const leftHalf = col < 5;
  const topHalf = row < 5;
  if (topHalf && leftHalf) return 'A';
  if (topHalf && !leftHalf) return 'S';
  if (!topHalf && leftHalf) return 'R';
  return 'T';
};

// Convert a profile's (x, y) in 0..100 to floating-point cell coordinates.
// Maps 0..100 → 0.5..9.5 (cell centers) so profiles never land at grid edges.
// y is inverted: y=100 → row 0 (top), y=0 → row 9 (bottom).
const profileToCellCoords = (x, y) => ({
  cx: 0.5 + (x / 100) * 9,
  cy: 0.5 + ((100 - y) / 100) * 9,
});

const getHeatmapCellColor = (quadrant, opacity, colors) =>
  hexToRgba(colors[quadrant] || ARTS_COLOR_FALLBACKS[quadrant], opacity);

// Map cell opacity (HEATMAP_MIN_OPACITY..HEATMAP_MAX_OPACITY) → integer 0..9.
// 0 means "no profile contributed" (cell at MIN_OPACITY floor).
// 1..9 mirrors the visual 9-step scale shown below the heatmap.
const opacityToIntensityStep = (opacity, hasContribution) => {
  if (!hasContribution) return 0;
  const range = HEATMAP_MAX_OPACITY - HEATMAP_MIN_OPACITY;
  if (range <= 0) return 0;
  const ratio = (opacity - HEATMAP_MIN_OPACITY) / range;
  const step = Math.min(9, Math.max(1, Math.ceil(ratio * 9 - 0.001)));
  return step;
};

const renderActiveShape = ({
  cx,
  cy,
  innerRadius,
  outerRadius,
  startAngle,
  endAngle,
  fill,
}) => (
  <g>
    <Sector
      cx={cx}
      cy={cy}
      innerRadius={innerRadius - 4}
      outerRadius={outerRadius + 8}
      startAngle={startAngle}
      endAngle={endAngle}
      fill={fill}
    />
  </g>
);

const getViewportWidth = () =>
  typeof window !== 'undefined' ? window.innerWidth : 0;

const getDonutLayout = (viewportWidth) => {
  if (viewportWidth < 360) {
    return { chartHeight: 225, innerRadius: 48, outerRadius: 78 };
  }
  if (viewportWidth < 480) {
    return { chartHeight: 240, innerRadius: 58, outerRadius: 92 };
  }
  return { chartHeight: 275, innerRadius: 78, outerRadius: 118 };
};

const splitTextInTwoLines = (text) => {
  const normalizedText = text ? text.trim() : '';
  if (!normalizedText) return ['', ''];

  const words = normalizedText.split(/\s+/);
  if (words.length === 1) return [normalizedText, ''];

  return [words.slice(0, -1).join(' '), words[words.length - 1]];
};

export const ARTSPreviewDonut = ({
  texts,
  colors,
  artsDistribution,
  isLoading,
  legendShowCount = false,
  legendHideEmpty = false,
}) => {
  const [activeIndex, setActiveIndex] = useState(null);
  const [viewportWidth, setViewportWidth] = useState(getViewportWidth);

  useEffect(() => {
    const handleResize = () => setViewportWidth(getViewportWidth());
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const donutLayout = useMemo(() => getDonutLayout(viewportWidth), [viewportWidth]);

  const data = useMemo(() => {
    if (!artsDistribution?.axes) return [];
    const mapped = artsDistribution.axes.map((axis) => ({
      discKey: axis.key,
      value: axis.totalByAxis,
      percentage: axis.percentage,
      color: colors[axis.key] || ARTS_COLOR_FALLBACKS[axis.key],
      label: texts.artsLabels[axis.key],
    }));
    return mapped.sort(
      (a, b) => ARTS_KEYS.indexOf(a.discKey) - ARTS_KEYS.indexOf(b.discKey),
    );
  }, [artsDistribution, colors, texts.artsLabels]);

  const total = artsDistribution?.grandTotal || 0;
  const isEmpty = !data.length || total === 0;

  const placeholderData = useMemo(
    () =>
      ARTS_KEYS.map((key) => ({
        discKey: key,
        value: 1,
        percentage: 0,
        color: hexToRgba(colors[key] || ARTS_COLOR_FALLBACKS[key], 0.12),
        label: texts.artsLabels[key],
      })),
    [colors, texts.artsLabels],
  );

  const displayData = isEmpty ? placeholderData : data;

  const activeSlice = !isEmpty && activeIndex !== null ? data[activeIndex] : null;
  const activeSlicePercentage = activeSlice ? activeSlice.percentage : null;
  const [donutTitleLineOne, donutTitleLineTwo] = useMemo(
    () => splitTextInTwoLines(texts.donutTitle),
    [texts.donutTitle],
  );

  return (
    <div className="card arts-preview-panel arts-preview-panel--donut">
      <div className="card-header arts-preview-panel__header">
        <div className="chart-header">
          <p>{texts.donutTitle}</p>
        </div>
        <div className="chart-header right">
          <p>{`${total} ${texts.profiles}`}</p>
        </div>
      </div>
      <div className="arts-preview-panel__body">
        {isLoading ? (
          <Loader />
        ) : (
          <>
            <div className="arts-preview-donut-chart">
              <ResponsiveContainer width="100%" height={donutLayout.chartHeight} minHeight={1}>
                <PieChart>
                  <Pie
                    data={displayData}
                    cx="50%"
                    cy="50%"
                    innerRadius={donutLayout.innerRadius}
                    outerRadius={donutLayout.outerRadius}
                    paddingAngle={2}
                    dataKey="value"
                    strokeWidth={0}
                    startAngle={180}
                    endAngle={-180}
                    activeIndex={!isEmpty && activeIndex !== null ? activeIndex : undefined}
                    activeShape={renderActiveShape}
                    onMouseEnter={(event, index) => !isEmpty && setActiveIndex(index)}
                    onMouseLeave={() => setActiveIndex(null)}
                    isAnimationActive={false}
                  >
                    {displayData.map((entry, index) => (
                      <Cell key={`arts-donut-cell-${entry.discKey}-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                </PieChart>
              </ResponsiveContainer>

              <div className="arts-preview-donut-chart__center">
                {activeSlice ? (
                  <>
                    <span className="arts-preview-donut-chart__value" style={{ color: activeSlice.color }}>
                      {`${activeSlicePercentage}%`}
                    </span>
                    <span className="arts-preview-donut-chart__label" style={{ color: activeSlice.color }}>
                      {activeSlice.label}
                    </span>
                    <span className="arts-preview-donut-chart__meta">
                      {`${activeSlice.value} ${texts.profiles}`}
                    </span>
                  </>
                ) : (
                  <span className="arts-preview-donut-chart__label arts-preview-donut-chart__label--two-lines">
                    <span>{donutTitleLineOne}</span>
                    {donutTitleLineTwo ? <span>{donutTitleLineTwo}</span> : null}
                  </span>
                )}
              </div>
            </div>

            <div className="arts-preview-legend">
              {ARTS_KEYS
                .map((artsKey) => {
                  const axis = data.find((d) => d.discKey === artsKey);
                  return {
                    artsKey,
                    count: axis ? axis.value : 0,
                    color: colors[artsKey] || ARTS_COLOR_FALLBACKS[artsKey],
                  };
                })
                .filter((item) => !legendHideEmpty || item.count > 0)
                .map((item) => (
                  <div key={`arts-donut-legend-${item.artsKey}`} className="arts-preview-legend__item">
                    <span
                      className="arts-preview-legend__dot"
                      style={{ backgroundColor: item.color }}
                    />
                    <span className="arts-preview-legend__text">
                      {legendShowCount ? `${item.artsKey}: ${item.count}` : item.artsKey}
                    </span>
                  </div>
                ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const ARTSPreviewScatter = ({ texts, colors, artsDistribution, isLoading }) => {
  const [containerNode, setContainerNode] = useState(null);
  const [dims, setDims] = useState({ width: 0, height: 0 });
  const [hovered, setHovered] = useState(null);
  const padding = 20;
  const tooltipMinWidth = 220;
  const tooltipEstimatedHeight = 140;
  const tooltipOffset = 12;
  const tooltipEdgePadding = 8;

  const points = useMemo(() => {
    if (!artsDistribution?.axes) return [];
    return artsDistribution.axes.flatMap((axis) =>
      axis.profiles.map((profile) => ({
        ...profile,
        axisKey: axis.key,
        axisName: axis.name,
      })),
    );
  }, [artsDistribution]);

  const grandTotal = artsDistribution?.grandTotal || 0;

  useEffect(() => {
    if (!containerNode) return;

    // Medición inicial inmediata
    const rect = containerNode.getBoundingClientRect();
    if (rect.width > 0 && rect.height > 0) {
      setDims({ width: rect.width, height: rect.height });
    }

    if (typeof ResizeObserver === 'undefined') return;

    const observer = new ResizeObserver((entries) => {
      entries.forEach((entry) => {
        setDims({
          width: entry.contentRect.width,
          height: entry.contentRect.height,
        });
      });
    });

    observer.observe(containerNode);
    return () => observer.disconnect();
  }, [containerNode]);

  const scaleX = useCallback(
    (value) => padding + (value / 100) * (dims.width - padding * 2),
    [dims.width],
  );
  const scaleY = useCallback(
    (value) => dims.height - padding - (value / 100) * (dims.height - padding * 2),
    [dims.height],
  );

  const tooltipPosition = useMemo(() => {
    if (!hovered) {
      return null;
    }

    let left = hovered.x + tooltipOffset;
    let top = hovered.y - tooltipEstimatedHeight - tooltipOffset;

    // Si no hay espacio a la derecha, mostramos el tooltip a la izquierda del punto.
    if (left + tooltipMinWidth > dims.width - tooltipEdgePadding) {
      left = hovered.x - tooltipMinWidth - tooltipOffset;
    }

    // Si no hay espacio arriba, lo mostramos debajo del punto.
    if (top < tooltipEdgePadding) {
      top = hovered.y + tooltipOffset;
    }

    left = Math.min(
      Math.max(left, tooltipEdgePadding),
      Math.max(
        tooltipEdgePadding,
        dims.width - tooltipMinWidth - tooltipEdgePadding,
      ),
    );

    top = Math.min(
      Math.max(top, tooltipEdgePadding),
      Math.max(
        tooltipEdgePadding,
        dims.height - tooltipEstimatedHeight - tooltipEdgePadding,
      ),
    );

    return { left, top };
  }, [dims.height, dims.width, hovered, tooltipEdgePadding, tooltipEstimatedHeight, tooltipMinWidth, tooltipOffset]);

  return (
    <div className="card arts-preview-panel arts-preview-panel--scatter">
      <div className="card-header arts-preview-panel__header">
        <div className="chart-header">
          <p>{texts.scatterTitle}</p>
        </div>
        <div className="chart-header right">
          <p>
            {`${grandTotal} ${
              grandTotal === 1 ? texts.person : texts.persons
            }`}
          </p>
        </div>
      </div>
      <div className="arts-preview-panel__body">
        {isLoading ? (
          <Loader />
        ) : (
          <>
            <div className="arts-preview-scatter" ref={setContainerNode}>
              <div className="arts-preview-scatter__quadrants">
                <div style={{ backgroundColor: hexToRgba(colors.A, 0.12) }} />
                <div style={{ backgroundColor: hexToRgba(colors.S, 0.12) }} />
                <div style={{ backgroundColor: hexToRgba(colors.R, 0.12) }} />
                <div style={{ backgroundColor: hexToRgba(colors.T, 0.12) }} />
              </div>

              <div className="arts-preview-scatter__labels">
                <span style={{ color: colors.A }} className="arts-preview-scatter__label arts-preview-scatter__label--a">A</span>
                <span style={{ color: colors.S }} className="arts-preview-scatter__label arts-preview-scatter__label--s">S</span>
                <span style={{ color: colors.R }} className="arts-preview-scatter__label arts-preview-scatter__label--r">R</span>
                <span style={{ color: colors.T }} className="arts-preview-scatter__label arts-preview-scatter__label--t">T</span>
              </div>

              {dims.width > 0 && dims.height > 0 && (
                <svg
                  className="arts-preview-scatter__svg"
                  width={dims.width}
                  height={dims.height}
                >
                  {points.map((point) => {
                    const pointColor = colors[point.axisKey] || ARTS_COLOR_FALLBACKS[point.axisKey];

                    return (
                      <circle
                        key={`arts-scatter-dot-${point.id}`}
                        cx={scaleX(point.x)}
                        cy={scaleY(point.y)}
                        r={5}
                        fill={pointColor}
                        fillOpacity={0.75}
                        stroke={pointColor}
                        strokeWidth={1}
                        strokeOpacity={0.9}
                        onMouseEnter={() => {
                          setHovered({
                            profile: point,
                            x: scaleX(point.x),
                            y: scaleY(point.y),
                            axisKey: point.axisKey,
                          });
                        }}
                        onMouseLeave={() => setHovered(null)}
                      />
                    );
                  })}
                </svg>
              )}

              {hovered && tooltipPosition && (
                <div
                  className="arts-preview-scatter__tooltip"
                  style={{
                    left: tooltipPosition.left,
                    top: tooltipPosition.top,
                    borderColor: colors[hovered.axisKey],
                  }}
                >
                  <span className="arts-preview-scatter__tooltip-key" style={{ color: colors[hovered.axisKey] }}>
                    {`${hovered.axisKey} · ${texts.artsLabels[hovered.axisKey]}`}
                  </span>
                  <span className="arts-preview-scatter__tooltip-name">{hovered.profile.name}</span>
                  {hovered.profile.persons.length > 0 && (
                    <ul
                      className="arts-preview-scatter__tooltip-persons"
                      style={{ '--arts-bullet-color': colors[hovered.axisKey] }}
                    >
                      {hovered.profile.persons.map((person, i) => (
                        <li key={i} className="arts-preview-scatter__tooltip-person">{person}</li>
                      ))}
                    </ul>
                  )}
                  <span className="arts-preview-scatter__tooltip-total">
                    {`${hovered.profile.total} ${texts.usersWithProfile}`}
                  </span>
                </div>
              )}
            </div>

            <div className="arts-preview-legend">
              {ARTS_KEYS.map((artsKey) => (
                <div key={`arts-scatter-legend-${artsKey}`} className="arts-preview-legend__item">
                  <span
                    className="arts-preview-legend__dot arts-preview-legend__dot--circle"
                    style={{ backgroundColor: colors[artsKey] }}
                  />
                  <span className="arts-preview-legend__text">{texts.artsLabels[artsKey]}</span>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const ARTSPreviewHeatmap = ({ texts, colors, artsDistribution, isLoading }) => {
  const [hoveredCell, setHoveredCell] = useState(null);
  const cellSize = 28;
  const totalSize = cellSize * 10;

  const { cells, isEmpty } = useMemo(() => {
    const grandTotal = artsDistribution?.grandTotal || 0;
    const empty = !grandTotal;

    // Flatten all profiles with their cell coordinates.
    const allProfiles = [];
    if (artsDistribution?.axes) {
      artsDistribution.axes.forEach((axis) => {
        (axis.profiles || []).forEach((profile) => {
          if (!profile.total || profile.total <= 0) return;
          const { cx, cy } = profileToCellCoords(profile.x, profile.y);
          allProfiles.push({
            cx,
            cy,
            weight: profile.total,
            quadrant: axis.key,
          });
        });
      });
    }

    // Bin-then-blur: first accumulate weights into cells, then Gaussian-blur
    // the grid. This ensures profile count is the PRIMARY intensity factor
    // and position only affects smooth transitions between cells.
    const binned = Array.from({ length: 10 }, () =>
      Array.from({ length: 10 }, () => 0),
    );
    for (let i = 0; i < allProfiles.length; i += 1) {
      const p = allProfiles[i];
      const col = Math.min(9, Math.max(0, Math.floor(p.cx)));
      const row = Math.min(9, Math.max(0, Math.floor(p.cy)));
      binned[row][col] = Math.max(binned[row][col], p.weight);
    }

    const sigma = computeAdaptiveSigma(grandTotal);
    const twoSigmaSq = 2 * sigma * sigma;
    const radiusCells = Math.ceil(HEATMAP_RADIUS);
    const intensity = Array.from({ length: 10 }, () =>
      Array.from({ length: 10 }, () => 0),
    );

    if (!empty) {
      for (let r = 0; r < 10; r += 1) {
        for (let c = 0; c < 10; c += 1) {
          if (binned[r][c] > 0) {
            // Cell with profile(s): use count directly (no blur).
            // Same count always produces same intensity.
            intensity[r][c] = binned[r][c];
          } else {
            // Empty cell: Gaussian blur from same-quadrant neighbors only.
            const cellQ = getCellQuadrant(c, r);
            let blur = 0;
            for (let dr = -radiusCells; dr <= radiusCells; dr += 1) {
              for (let dc = -radiusCells; dc <= radiusCells; dc += 1) {
                if (dr === 0 && dc === 0) continue;
                const nr = r + dr;
                const nc = c + dc;
                if (nr < 0 || nr > 9 || nc < 0 || nc > 9) continue;
                if (getCellQuadrant(nc, nr) !== cellQ) continue;
                if (binned[nr][nc] <= 0) continue;
                const distSq = dr * dr + dc * dc;
                if (distSq > HEATMAP_RADIUS * HEATMAP_RADIUS) continue;
                blur += binned[nr][nc] * Math.exp(-distSq / twoSigmaSq);
              }
            }
            intensity[r][c] = blur;
          }
        }
      }
    }

    // Max per quadrant from PROFILE cells only (binned > 0).
    // Blur cells are visual filler and must not affect normalization.
    const quadrantMax = { R: 0, A: 0, T: 0, S: 0 };
    for (let r = 0; r < 10; r += 1) {
      for (let c = 0; c < 10; c += 1) {
        if (binned[r][c] <= 0) continue;
        const q = getCellQuadrant(c, r);
        if (binned[r][c] > quadrantMax[q]) quadrantMax[q] = binned[r][c];
      }
    }

    // Build cells with opacity. Each cell is normalized against its own
    // quadrant's max so every quadrant's hottest cell reaches 9/9.
    const builtCells = [];
    for (let r = 0; r < 10; r += 1) {
      for (let c = 0; c < 10; c += 1) {
        const quadrant = getCellQuadrant(c, r);
        let opacity;
        const qMax = quadrantMax[quadrant];
        if (empty || qMax <= 0) {
          opacity = HEATMAP_MIN_OPACITY;
        } else {
          // Cap blur cells so they never exceed 50% of the quadrant max.
          const value = binned[r][c] > 0
            ? binned[r][c]
            : Math.min(intensity[r][c], qMax * 0.5);
          const ratio = value / qMax;
          const curved = Math.pow(ratio, HEATMAP_GAMMA);
          opacity = HEATMAP_MIN_OPACITY + curved * (HEATMAP_MAX_OPACITY - HEATMAP_MIN_OPACITY);
        }
        builtCells.push({
          col: c,
          row: r,
          quadrant,
          opacity,
          intensity: intensity[r][c],
          hasContribution: intensity[r][c] > 0,
        });
      }
    }

    return {
      cells: builtCells,
      isEmpty: empty,
    };
  }, [artsDistribution]);

  return (
    <div className="card arts-preview-panel arts-preview-panel--heatmap">
      <div className="card-header arts-preview-panel__header">
        <div className="chart-header">
          <p>{texts.heatmapTitle}</p>
        </div>
      </div>
      <div className="arts-preview-panel__body">
        {isLoading ? (
          <Loader />
        ) : (
          <div className="arts-preview-heatmap">
          <div className="arts-preview-heatmap__axis arts-preview-heatmap__axis--top" style={{ width: totalSize }}>
            <span style={{ color: colors.A }}>A</span>
            <span style={{ color: colors.S }}>S</span>
          </div>

          <div className="arts-preview-heatmap__grid" style={{ width: totalSize, height: totalSize }}>
            {cells.map((cell) => {
              const opacity = isEmpty ? HEATMAP_MIN_OPACITY : cell.opacity;
              const color = getHeatmapCellColor(cell.quadrant, opacity, colors);
              const isActive =
                hoveredCell && hoveredCell.row === cell.row && hoveredCell.col === cell.col;

              return (
                <button
                  key={`arts-heatmap-cell-${cell.row}-${cell.col}`}
                  type="button"
                  className="arts-preview-heatmap__cell"
                  style={{
                    left: cell.col * cellSize,
                    top: cell.row * cellSize,
                    width: cellSize - 1,
                    height: cellSize - 1,
                    backgroundColor: color,
                    borderColor: isActive ? '#344563' : 'rgba(255, 255, 255, 0.3)',
                  }}
                  onMouseEnter={() => setHoveredCell(cell)}
                  onMouseLeave={() => setHoveredCell(null)}
                />
              );
            })}

            <div className="arts-preview-heatmap__divider arts-preview-heatmap__divider--vertical" style={{ left: totalSize / 2 }} />
            <div className="arts-preview-heatmap__divider arts-preview-heatmap__divider--horizontal" style={{ top: totalSize / 2 }} />
          </div>

          <div className="arts-preview-heatmap__axis arts-preview-heatmap__axis--bottom" style={{ width: totalSize }}>
            <span style={{ color: colors.R }}>R</span>
            <span style={{ color: colors.T }}>T</span>
          </div>

          <div className="arts-preview-heatmap__summary-slot">
            {hoveredCell && (
              <div className="arts-preview-heatmap__summary">
                <span
                  className="arts-preview-heatmap__summary-dot"
                  style={{
                    backgroundColor: getHeatmapCellColor(
                      hoveredCell.quadrant,
                      hoveredCell.opacity,
                      colors,
                    ),
                  }}
                />
                <span>
                  {`${hoveredCell.quadrant} · ${texts.intensityLabel} ${opacityToIntensityStep(hoveredCell.opacity, hoveredCell.hasContribution)}/9`}
                </span>
              </div>
            )}
          </div>

          <div className="arts-preview-heatmap__scale">
            <span className="arts-preview-heatmap__scale-label">{texts.scaleLow}</span>
            <div className="arts-preview-heatmap__scale-bar">
              {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((step) => (
                <span
                  key={`arts-heatmap-scale-${step}`}
                  className="arts-preview-heatmap__scale-step"
                  style={{
                    backgroundColor: hexToRgba(
                      colors.R,
                      HEATMAP_MIN_OPACITY +
                        (step / 9) * (HEATMAP_MAX_OPACITY - HEATMAP_MIN_OPACITY),
                    ),
                  }}
                />
              ))}
            </div>
            <span className="arts-preview-heatmap__scale-label">{texts.scaleHigh}</span>
          </div>

          <div className="arts-preview-legend arts-preview-legend--heatmap">
            {ARTS_KEYS.map((artsKey) => (
              <div key={`arts-heatmap-legend-${artsKey}`} className="arts-preview-legend__item">
                <span
                  className="arts-preview-legend__dot"
                  style={{ backgroundColor: colors[artsKey] }}
                />
                <span className="arts-preview-legend__text">{texts.artsLabels[artsKey]}</span>
              </div>
            ))}
          </div>
          </div>
        )}
      </div>
    </div>
  );
};

const ARTSPreviewCharts = ({ texts, artsDistribution, artsDistributionStatus }) => {
  const colors = getArtsColors();
  const isLoading =
    artsDistributionStatus?.loading ||
    (!artsDistribution && !artsDistributionStatus?.error);

  return (
    <div className="row arts-preview-grid">
      <div className="col-md-12 col-lg-4 arts-preview-grid__col">
        <ARTSPreviewDonut texts={texts} colors={colors} artsDistribution={artsDistribution} isLoading={isLoading} />
      </div>
      <div className="col-md-12 col-lg-4 arts-preview-grid__col">
        <ARTSPreviewScatter texts={texts} colors={colors} artsDistribution={artsDistribution} isLoading={isLoading} />
      </div>
      <div className="col-md-12 col-lg-4 arts-preview-grid__col">
        <ARTSPreviewHeatmap texts={texts} colors={colors} artsDistribution={artsDistribution} isLoading={isLoading} />
      </div>
    </div>
  );
};

export default ARTSPreviewCharts;
